import chevronDown from '@jetbrains/icons/chevron-down.svg'
import chevronRight from '@jetbrains/icons/chevron-right.svg'
import Icon from '@jetbrains/ring-ui/components/icon/icon'
import classNames from 'classnames'
import * as React from 'react'

import type {Branch} from '../../../../../../../services/rest'
import type {BuildId, BuildTypeId} from '../../../../../../../types'
import {noop} from '../../../../../../../utils/empty'
import BuildTypeBranch from '../../../../../../common/BranchLabel/BuildTypeBranchLabel.container'
import BuildsWrapped from '../../../../../../common/Builds/Builds.wrapped'
import Details from '../../../../../../common/Details/Details'
import RowGroup from '../../../../../../common/Grid/RowGroup'
import RunBuildButtonGroup from '../../../../../../common/RunBuild/RunBuild.container'
import PendingChangesDropdown from '../../../../../../packages/Changes/PendingChangesDropdown/PendingChangesDropdown'

import styles from './CollapsibleBranchLine.css'

type DefaultProps = {
  oneLine: boolean
  queuedVisible: boolean
  hideQueuedBuildsForBranch: (buildTypeId: BuildTypeId, branch: Branch) => unknown
  showQueuedBuildsForBranch: (buildTypeId: BuildTypeId, branch: Branch) => unknown
}
type Props = DefaultProps & {
  branch: Branch | null | undefined
  queuedBuildIds: ReadonlyArray<BuildId>
  nonQueuedBuildIds: ReadonlyArray<BuildId>
  buildTypeId: BuildTypeId
  isVisible?: boolean
  renderWhenVisible?: boolean
}
type State = {
  expanded: boolean
  hasBeenShown: boolean
}

class CollapsibleBranchLine extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    oneLine: false,
    queuedVisible: false,
    hideQueuedBuildsForBranch: noop,
    showQueuedBuildsForBranch: noop,
  }

  static getDerivedStateFromProps(
    {isVisible}: Props,
    {hasBeenShown}: State,
  ): Partial<State> | null | undefined {
    if (isVisible === true && !hasBeenShown) {
      return {
        hasBeenShown: true,
      }
    }

    return null
  }

  state: State = {
    expanded: true,
    hasBeenShown: !!this.props.isVisible,
  }

  shouldComponentUpdate(nextProps: Props): boolean {
    return !(this.props.isVisible === false && nextProps.isVisible === false)
  }

  toggleDetails: () => void = () => {
    this.setState(state => ({
      expanded: !state.expanded,
    }))
  }

  handleQueuedToggle: () => void = () => {
    const {
      queuedVisible,
      hideQueuedBuildsForBranch,
      showQueuedBuildsForBranch,
      buildTypeId,
      branch,
    } = this.props

    if (branch == null) {
      return
    }

    if (queuedVisible) {
      hideQueuedBuildsForBranch(buildTypeId, branch)
    } else {
      showQueuedBuildsForBranch(buildTypeId, branch)
    }
  }

  getSummary: () => React.ReactNode = () => {
    const {
      buildTypeId,
      branch,
      queuedBuildIds,
      nonQueuedBuildIds,
      oneLine,
      isVisible,
      renderWhenVisible,
    } = this.props
    const {hasBeenShown} = this.state
    const readyToRender = renderWhenVisible === true ? isVisible === true || hasBeenShown : true
    const headerClasses = classNames(oneLine && styles.oneLine, styles.header)
    return (
      <RowGroup>
        <div className={headerClasses} data-test-branch-line>
          <div className={styles.title}>
            <BuildTypeBranch
              name={branch?.name}
              main={branch?.default ?? false}
              buildTypeId={buildTypeId}
              className={classNames(
                styles.branch,
                queuedBuildIds.length > 0 && styles.withQueuedBuild,
              )}
            />
          </div>
          <div className={styles.changes}>
            {readyToRender ? (
              <PendingChangesDropdown buildTypeId={buildTypeId} branch={branch} />
            ) : null}
          </div>
          <div className={styles.runColumn}>
            {readyToRender ? (
              <RunBuildButtonGroup buildTypeId={buildTypeId} branch={branch} />
            ) : null}
          </div>
          {(queuedBuildIds.length + nonQueuedBuildIds.length > 0 && (
            <Icon
              glyph={this.state.expanded ? chevronDown : chevronRight}
              className={classNames(styles.chevron)}
            />
          )) ||
            null}
        </div>
      </RowGroup>
    )
  }

  getPlaceholderItemsCount: () => number = (): number =>
    this.props.queuedVisible
      ? this.props.queuedBuildIds.length + this.props.nonQueuedBuildIds.length
      : this.props.nonQueuedBuildIds.length + 1

  getPlaceholderItems: () => React.ReactNode = () => {
    const {oneLine} = this.props
    const result = []
    const itemsCount = this.getPlaceholderItemsCount()
    const placeholderStyle = classNames(oneLine && styles.oneLine, styles.placeholderItem)
    const content = <span>{'Loading...'}</span>

    for (let i = 0; i < itemsCount; i++) {
      result.push(
        <div key={i} className={placeholderStyle}>
          {i === 0 ? content : null}
        </div>,
      )
    }

    return result
  }

  getBuildsSection: () => React.ReactNode = () => {
    const {nonQueuedBuildIds, queuedBuildIds, queuedVisible} = this.props
    return (
      <BuildsWrapped
        hasParentGrid
        queuedBuildIds={queuedBuildIds}
        buildIds={nonQueuedBuildIds}
        onToggle={this.handleQueuedToggle}
        queuedVisible={queuedVisible}
        queuedTogglerHidden={nonQueuedBuildIds.length === 0 && queuedBuildIds.length > 0}
      />
    )
  }

  getContent: () => React.ReactNode = () => {
    const {queuedBuildIds, nonQueuedBuildIds, isVisible, oneLine, renderWhenVisible} = this.props
    const {hasBeenShown} = this.state

    if (queuedBuildIds.length === 0 && nonQueuedBuildIds.length === 0) {
      return null
    }

    if (renderWhenVisible === true) {
      return (
        ((isVisible === true || hasBeenShown || nonQueuedBuildIds.length === 0) &&
          this.getBuildsSection()) || (
          <div className={classNames(oneLine && styles.oneLine, styles.placeholder)}>
            {this.getPlaceholderItems()}
          </div>
        )
      )
    }

    return this.getBuildsSection()
  }

  render(): React.ReactNode {
    const {branch} = this.props
    return (
      branch?.name != null && (
        <Details
          open={this.state.expanded}
          label={branch.name}
          summary={this.getSummary()}
          onToggle={this.toggleDetails}
          headingClassName={styles.root}
          summaryClassName={styles.button}
          detailsWrapperClassName={styles.details}
          detailsClassName={styles.details}
          renderButtonAside
          imitateHeading
          level={2}
        >
          {this.getContent()}
        </Details>
      )
    )
  }
}

export default CollapsibleBranchLine
